import React, { useState } from "react";
import {
  Card,
  CardContent
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Route as salesOneRoute } from "@/routes/(pharma)/_layout/overview/sales_history/$saleId";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { CaretSortIcon } from "@radix-ui/react-icons"; // Ensure you have this icon
import { RefundMode, SaleStatus } from "@/gen/sales/common_pb";
import { ItemType } from "@/gen/inventory/common_pb";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export default function Items({
  partialMedicalItemsSelected,
  setPartialMedicalItemsSelected,
}: {
  partialMedicalItemsSelected: {
    saleItemId: string;
    itemType: ItemType;
    quantity: number;
  }[];
  setPartialMedicalItemsSelected: React.Dispatch<
    React.SetStateAction<{
      saleItemId: string;
      itemType: ItemType;
      quantity: number;
    }[]>
  >;
}) {
  const data = salesOneRoute.useLoaderData();

  // Sorting state
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("expiry_date");

  // Sort function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Card className="w-full border-none shadow-none overflow-auto">
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="h-[40px] m-0">
              <TableHead className="sm:table-cell">
                <Checkbox
                  className="h-4 w-4 border border-gray-500 rounded-sm"
                  checked={
                    partialMedicalItemsSelected.length === data.items.length
                  }
                  disabled={data.saleValues?.status === SaleStatus.FULLY_REFUNDED || data.saleValues?.status === SaleStatus.VOIDED}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setPartialMedicalItemsSelected(
                        data.items.map((i) => {
                          return {
                            saleItemId: i.saleItemId,
                            itemType: i.itemType,
                            quantity: i.quantity,
                          };
                        })
                      );
                    } else {
                      setPartialMedicalItemsSelected([]);
                    }
                  }}
                ></Checkbox>
              </TableHead>
              <TableHead className="sm:table-cell">Item Name</TableHead>
              {/* <TableHead className="sm:table-cell">Barcode</TableHead> */}
              <TableHead className="sm:table-cell">Batch Number</TableHead>
              <TableHead
                className="sm:table-cell cursor-pointer"
                onClick={() => handleRequestSort("expiry_date")}
              >
                <div className="flex items-center">
                  <span>Expiry Date</span>
                  {orderBy === "expiry_date" && (
                    <CaretSortIcon
                      className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""
                        }`}
                    />
                  )}
                </div>
              </TableHead>
              <TableHead className="sm:table-cell">Quantity</TableHead>
              <TableHead className="sm:table-cell">Price</TableHead>
              <TableHead className="sm:table-cell">Gross</TableHead>
              <TableHead className="sm:table-cell">Tax</TableHead>
              <TableHead className="sm:table-cell">Net</TableHead>
              {(data.saleValues?.status === SaleStatus.FULLY_REFUNDED || data.saleValues?.status === SaleStatus.PARTIALLY_REFUNDED) && <TableHead className="sm:table-cell">Refund Information</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((item, index) => (
              <TableRow className="h-[49px]" key={index}>
                <TableCell>
                  <Checkbox
                    className="h-4 w-4 border border-gray-500 rounded-sm"
                    checked={
                      partialMedicalItemsSelected.map(
                        (i) => i.saleItemId === item.saleItemId &&
                        i.itemType === item.itemType &&
                        i.quantity === item.quantity
                      ).includes(true)
                    }
                    disabled={data.saleValues?.status === SaleStatus.FULLY_REFUNDED || data.saleValues?.status === SaleStatus.VOIDED || item.isRefunded}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setPartialMedicalItemsSelected([
                          ...partialMedicalItemsSelected,
                          {
                            saleItemId: item.saleItemId,
                            itemType: item.itemType,
                            quantity: item.quantity,
                          }
                        ]);
                      } else {
                        setPartialMedicalItemsSelected(
                          partialMedicalItemsSelected.filter(
                            (id) => {
                              return (
                                id.saleItemId !== item.saleItemId ||
                                id.itemType !== item.itemType ||
                                id.quantity !== item.quantity
                              );
                            }
                          )
                        );
                      }
                    }}
                  ></Checkbox>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{item.tradeName} ({item.unit} {item.unitType})</div>
                </TableCell>
                {/* <TableCell className="hidden sm:table-cell font-medium">
                  {item.ba ? item.barcode : "N/A"}
                </TableCell> */}
                <TableCell className="hidden sm:table-cell font-medium">
                  {item.batchNumber}
                </TableCell>
                <TableCell className="hidden md:table-cell font-medium">
                  {dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="hidden sm:table-cell font-medium">
                  {item.quantity}
                </TableCell>
                <TableCell className="hidden sm:table-cell font-medium">
                  {convertToCurrencyRounded(item.unitPrice)}
                </TableCell>
                <TableCell className="hidden sm:table-cell font-medium">
                  {
                    convertToCurrencyRounded(
                      item.taxableAmount
                    )
                  }
                </TableCell>
                <TableCell className="hidden sm:table-cell font-medium">
                  {
                    convertToCurrencyRounded(
                      item.totalTaxAmount
                    )
                  }
                </TableCell>
                <TableCell className="hidden sm:table-cell font-medium">
                  {convertToCurrencyRounded(item.totalAmount)}
                </TableCell>
                {(data.saleValues?.status === SaleStatus.FULLY_REFUNDED || data.saleValues?.status === SaleStatus.PARTIALLY_REFUNDED) &&
                  <TableCell className="hidden sm:table-cell font-medium">
                  {item.isRefunded ? (
                    <Popover>
                      <PopoverTrigger>
                        <span className="text-green-600">Refunded</span>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="p-4">
                          <p className="font-medium">Refund Details</p>
                          <p>Refund Mode: {Object.keys(RefundMode).find(
                            key => RefundMode[key as keyof typeof RefundMode] === item.refundMode
                          ) || "Unknown"}</p>
                          <p>Refunded By: {item.refundedBy?.fullName} ({item.refundedBy?.username})</p>
                          <p>Refunded At: {dayjs(item.refundedAt?.toDate()).format("DD/MM/YYYY HH:mm")}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span className="text-red-600">Not Refunded</span>
                  )}
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
